/**
 * Team Boards — API client + React Query hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import type {
  BoardAttachment,
  BoardNotification,
  BoardColumn,
  BoardFull,
  BoardGroup,
  BoardRow,
  BoardSummary,
  ColumnKind,
  ColumnConfig,
} from './types';

const BASE = '/api/team-boards';
const API_ORIGIN = (import.meta.env.VITE_API_URL as string) || 'https://myapp.chatelainxpert.be';
const authToken = () => localStorage.getItem('authToken') || '';

interface ApiResp<T> { success: boolean; data: T; message?: string }

// ==================== Users (for Person picker) ====================

export interface BoardUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

export const useUsersForBoards = () => useQuery({
  queryKey: ['team-boards', 'users'],
  queryFn: async (): Promise<BoardUser[]> => {
    const r = await apiClient.get<ApiResp<BoardUser[]>>('/api/admin/mes-taches/users');
    return r.data;
  },
  staleTime: 5 * 60_000, // users rarely change
});

// ==================== Boards ====================

export const useBoards = () => useQuery({
  queryKey: ['team-boards'],
  queryFn: async (): Promise<BoardSummary[]> => {
    const r = await apiClient.get<ApiResp<BoardSummary[]>>(BASE);
    return r.data;
  },
  staleTime: 30_000,
});

export const useBoard = (id: string | null) => useQuery({
  queryKey: ['team-boards', id],
  queryFn: async (): Promise<BoardFull> => {
    const r = await apiClient.get<ApiResp<BoardFull>>(`${BASE}/${id}`);
    return r.data;
  },
  enabled: !!id,
  staleTime: 10_000,
});

export const useCreateBoard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; description?: string; icon?: string; visibility?: 'private' | 'shared' }) => {
      const r = await apiClient.post<ApiResp<BoardSummary>>(BASE, payload);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards'] }),
  });
};

export const useUpdateBoard = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<{ name: string; description: string; icon: string; is_archived: boolean; visibility: 'private' | 'shared' }>) => {
      const r = await apiClient.put<ApiResp<BoardSummary>>(`${BASE}/${id}`, payload);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-boards'] });
      qc.invalidateQueries({ queryKey: ['team-boards', id] });
    },
  });
};

export const useDeleteBoard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${BASE}/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards'] }),
  });
};

// ==================== Columns ====================

export const useCreateColumn = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; kind: ColumnKind; config?: ColumnConfig }) => {
      const r = await apiClient.post<ApiResp<BoardColumn>>(`${BASE}/${boardId}/columns`, payload);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

export const useUpdateColumn = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ columnId, ...payload }: { columnId: string; name?: string; config?: ColumnConfig; position?: number; width?: number }) => {
      const r = await apiClient.put<ApiResp<BoardColumn>>(`${BASE}/${boardId}/columns/${columnId}`, payload);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

export const useDeleteColumn = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (columnId: string) => {
      await apiClient.delete(`${BASE}/${boardId}/columns/${columnId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

/** Reorder columns: persists each column's new position (no batch endpoint). Optimistic. */
export const useReorderColumns = (boardId: string) => {
  const qc = useQueryClient();
  const key = ['team-boards', boardId];
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await Promise.all(orderedIds.map((id, i) =>
        apiClient.put(`${BASE}/${boardId}/columns/${id}`, { position: i })));
    },
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<BoardFull>(key);
      if (prev) {
        const byId = new Map(prev.columns.map(c => [c.id, c]));
        const cols = orderedIds
          .map((id, i) => { const c = byId.get(id); return c ? { ...c, position: i } : null; })
          .filter(Boolean) as typeof prev.columns;
        qc.setQueryData<BoardFull>(key, { ...prev, columns: cols });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
};

// ==================== Groups ====================

export const useCreateGroup = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; color?: string }) => {
      const r = await apiClient.post<ApiResp<BoardGroup>>(`${BASE}/${boardId}/groups`, payload);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

export const useUpdateGroup = (boardId: string) => {
  const qc = useQueryClient();
  const key = ['team-boards', boardId];
  return useMutation({
    // NOTE: the backend service destructures camelCase `isCollapsed`, so the
    // body must use `isCollapsed` (not snake_case) for collapse to persist.
    mutationFn: async ({ groupId, ...payload }: { groupId: string; name?: string; color?: string; isCollapsed?: boolean }) => {
      const r = await apiClient.put<ApiResp<BoardGroup>>(`${BASE}/${boardId}/groups/${groupId}`, payload);
      return r.data;
    },
    // Optimistic so the chevron/name/color flips instantly without a refetch flash.
    onMutate: async ({ groupId, name, color, isCollapsed }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<BoardFull>(key);
      if (prev) {
        qc.setQueryData<BoardFull>(key, {
          ...prev,
          groups: prev.groups.map(g => g.id === groupId ? {
            ...g,
            name: name ?? g.name,
            color: color ?? g.color,
            is_collapsed: isCollapsed ?? g.is_collapsed,
          } : g),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
};

export const useDeleteGroup = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string) => {
      await apiClient.delete(`${BASE}/${boardId}/groups/${groupId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

// ==================== Rows ====================

export const useCreateRow = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { groupId?: string | null; values?: Record<string, unknown> }) => {
      const r = await apiClient.post<ApiResp<BoardRow>>(`${BASE}/${boardId}/rows`, payload);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

export const useUpdateRow = (boardId: string) => {
  const qc = useQueryClient();
  const key = ['team-boards', boardId];
  return useMutation({
    mutationFn: async ({ rowId, ...payload }: { rowId: string; values?: Record<string, unknown>; groupId?: string | null }) => {
      const r = await apiClient.put<ApiResp<BoardRow>>(`${BASE}/${boardId}/rows/${rowId}`, payload);
      return r.data;
    },
    // Optimistic: patch the board cache in place so cells stop flashing on blur.
    onMutate: async ({ rowId, values, groupId }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<BoardFull>(key);
      if (prev) {
        qc.setQueryData<BoardFull>(key, {
          ...prev,
          rows: prev.rows.map(r =>
            r.id === rowId
              ? {
                  ...r,
                  values: values ? { ...r.values, ...values } : r.values,
                  group_id: groupId !== undefined ? groupId : r.group_id,
                }
              : r
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    // No invalidate on success: the optimistic patch already shows the new state
    // and the server returns the row we already wrote. Skip the refetch flash.
  });
};

export const useDeleteRow = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rowId: string) => {
      await apiClient.delete(`${BASE}/${boardId}/rows/${rowId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

/**
 * Bulk reorder rows. Optimistic: rewrites positions in the cache, then
 * POSTs the new order to /rows/reorder.
 */
export const useReorderRows = (boardId: string) => {
  const qc = useQueryClient();
  const key = ['team-boards', boardId];
  return useMutation({
    mutationFn: async (items: { id: string; position: number; group_id: string | null }[]) => {
      await apiClient.post(`${BASE}/${boardId}/rows/reorder`, { items });
    },
    onMutate: async (items) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<BoardFull>(key);
      if (prev) {
        const posByRow = new Map(items.map(it => [it.id, it]));
        qc.setQueryData<BoardFull>(key, {
          ...prev,
          rows: prev.rows.map(r => {
            const upd = posByRow.get(r.id);
            return upd ? { ...r, position: upd.position, group_id: upd.group_id } : r;
          }),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });
};

// ==================== Bulk delete rows ====================

export const useBulkDeleteRows = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rowIds: string[]) => {
      await Promise.all(rowIds.map(id => apiClient.delete(`${BASE}/${boardId}/rows/${id}`)));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

// ==================== Attachments ====================
// Uploads use raw fetch (not the axios wrapper) so the browser sets the
// multipart boundary itself. Downloads stream through an authenticated GET
// that the UI turns into a blob: URL (so <img>/<iframe> work without leaking
// the bearer token in a query string).

export const useUploadAttachments = (boardId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rowId, files }: { rowId: string; files: File[] | FileList }) => {
      const form = new FormData();
      Array.from(files).forEach(f => form.append('files', f));
      const res = await fetch(`${API_ORIGIN}${BASE}/${boardId}/rows/${rowId}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken()}` },
        body: form,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const json = await res.json();
      return json.data as BoardAttachment[];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

export const useDeleteAttachment = (boardId: string) => {
  const qc = useQueryClient();
  const key = ['team-boards', boardId];
  return useMutation({
    mutationFn: async (attId: string) => {
      await apiClient.delete(`${BASE}/${boardId}/attachments/${attId}`);
    },
    // Optimistic: splice the attachment out so the ItemPanel / Documents column
    // update instantly. Also drop & revoke any cached blob URL for it.
    onMutate: async (attId: string) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<BoardFull>(key);
      if (prev) {
        qc.setQueryData<BoardFull>(key, {
          ...prev,
          rows: prev.rows.map(r => r.attachments?.some(a => a.id === attId)
            ? { ...r, attachments: r.attachments.filter(a => a.id !== attId) }
            : r),
        });
      }
      revokeBlobUrl(boardId, attId);
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-boards', boardId] }),
  });
};

// Cache of attachment-id → { promise, url } so each file is fetched once and the
// object URL can later be revoked (avoids unbounded memory growth in a session).
const blobUrlCache = new Map<string, { promise: Promise<string>; url?: string }>();
const blobKey = (boardId: string, attId: string) => `${boardId}:${attId}`;

export const attachmentBlobUrl = (boardId: string, attId: string): Promise<string> => {
  const key = blobKey(boardId, attId);
  let entry = blobUrlCache.get(key);
  if (!entry) {
    const promise = fetch(`${API_ORIGIN}${BASE}/${boardId}/attachments/${attId}/file`, {
      headers: { Authorization: `Bearer ${authToken()}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then(b => {
        const url = URL.createObjectURL(b);
        const e = blobUrlCache.get(key);
        if (e) e.url = url;
        return url;
      })
      .catch(err => { blobUrlCache.delete(key); throw err; });
    entry = { promise };
    blobUrlCache.set(key, entry);
  }
  return entry.promise;
};

export const revokeBlobUrl = (boardId: string, attId: string) => {
  const key = blobKey(boardId, attId);
  const e = blobUrlCache.get(key);
  if (e?.url) URL.revokeObjectURL(e.url);
  blobUrlCache.delete(key);
};

/** Revoke every cached blob URL (optionally only for one board). Call on board switch / unmount. */
export const clearBlobUrlCache = (boardId?: string) => {
  for (const [key, e] of blobUrlCache) {
    if (boardId && !key.startsWith(`${boardId}:`)) continue;
    if (e.url) URL.revokeObjectURL(e.url);
    blobUrlCache.delete(key);
  }
};

export const attachmentDownloadUrl = (boardId: string, attId: string) =>
  `${API_ORIGIN}${BASE}/${boardId}/attachments/${attId}/file?download=1`;

// ==================== Notifications ====================

export const NOTIF_KEY = ['team-boards', 'notifications'];

export const useNotifications = (enabled = true) => useQuery({
  queryKey: NOTIF_KEY,
  queryFn: async (): Promise<{ items: BoardNotification[]; unread: number }> => {
    const r = await apiClient.get<ApiResp<{ items: BoardNotification[]; unread: number }>>(`${BASE}/notifications`);
    return r.data;
  },
  enabled,
  staleTime: 20_000,
});

export const useMarkNotificationRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.post(`${BASE}/notifications/${id}/read`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
};

export const useMarkAllNotificationsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => { await apiClient.post(`${BASE}/notifications/read-all`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
};

export const useDeleteNotification = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`${BASE}/notifications/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
};

export const useClearAllNotifications = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => { await apiClient.delete(`${BASE}/notifications`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });
};
